import { toValue, watch, type MaybeRefOrGetter } from 'vue'

type SeedableForm<T extends object> = {
  setValues: (values: Partial<T>) => void
  resetForm: () => void
}

/**
 * Fills an edit form from the record it edits.
 *
 * Two things have to happen: the form is seeded once the record arrives, and it is
 * reset and re-seeded every time the modal is reopened (otherwise the previous
 * record's values leak into the next one). `toForm` is the single place the record is
 * mapped onto form fields — never hand the raw entity to `setValues`, or untyped keys
 * end up in the state.
 */
export function useFormSeed<T extends object, TItem>(
  form: SeedableForm<T>,
  source: MaybeRefOrGetter<TItem | null | undefined>,
  toForm: (item: TItem) => Partial<T>,
  isOpen?: MaybeRefOrGetter<boolean | undefined>
) {
  /**
   * Which record the fields currently hold, so the same one is never written over them
   * twice. Seeding is about *arrival* — the query resolves after the modal opens — and
   * about *a different record*, never about the record changing underneath.
   *
   * Told apart by id and **not** by object identity: TanStack hands back a new response
   * object on every refetch, so identity would let any refetch of the detail query
   * overwrite what the user has typed, mid-edit and with no mask on screen. A record with
   * no `id` has nothing else to key on and falls back to identity.
   */
  let seededKey: unknown

  const keyOf = (item: TItem) =>
    (item as { id?: unknown })?.id ?? (item as unknown)

  function seed() {
    const item = toValue(source)

    if (!item) return

    seededKey = keyOf(item)
    form.setValues(toForm(item))
  }

  watch(
    () => toValue(source),
    (item) => {
      if (item && keyOf(item) === seededKey) return

      seed()
    },
    { immediate: true }
  )

  if (isOpen !== undefined) {
    watch(
      () => toValue(isOpen),
      (open) => {
        if (!open) return

        // Reopening is a fresh edit of whatever the modal now points at, so the guard
        // above must not treat the previous record as already seeded.
        seededKey = undefined
        form.resetForm()
        seed()
      },
      { immediate: true }
    )
  }

  return { seed }
}
